/**
 * Pure helpers for the AI assistant settings in the inbox (unit tested). API: GET/PUT /v1/whatsapp-inbox/settings/assistant
 * and the knowledge-base FAQs GET/PUT /v1/knowledge/faqs. The weekly editor shows one opening range per day; a closing
 * time earlier than the opening time is an overnight range (the API understands it the same way).
 */
import {
  AssistantSettingsSchema, DEFAULT_ASSISTANT_SETTINGS, MAX_HANDOVER_KEYWORDS, STYLE_NOTE_MAX, WEEKDAYS, type AssistantSettings, type HoursRange,
} from '@qm/contracts/whatsapp-assistant';
import { MAX_FAQS, type FaqItem } from '@qm/contracts/knowledge';

export const ASSISTANT_PATH = '/v1/whatsapp-inbox/settings/assistant' as const;
export const FAQS_PATH = '/v1/knowledge/faqs' as const;
export { MAX_FAQS, MAX_HANDOVER_KEYWORDS, STYLE_NOTE_MAX };

export const TIMEZONES = ['Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Kuwait', 'Asia/Bahrain', 'Asia/Muscat', 'Africa/Cairo', 'Europe/London', 'UTC'] as const;
export const timezonesWith = (current: string): string[] => (TIMEZONES as readonly string[]).includes(current) ? [...TIMEZONES] : [current, ...TIMEZONES];

/** The settings from a GET/PUT response; anything missing or invalid falls back to the defaults. */
export function assistantOf(data: unknown): AssistantSettings {
  const s = data && typeof data === 'object' ? (data as { settings?: unknown }).settings : undefined;
  const parsed = AssistantSettingsSchema.safeParse(s ?? {});
  return parsed.success ? parsed.data : DEFAULT_ASSISTANT_SETTINGS;
}
export function faqsOf(data: unknown): FaqItem[] {
  const items = data && typeof data === 'object' ? (data as { items?: unknown }).items : undefined;
  return Array.isArray(items) ? items.filter((f): f is FaqItem => !!f && typeof f.question === 'string' && typeof f.answer === 'string').map((f) => ({ question: f.question, answer: f.answer })) : [];
}

export interface DayRow { day: number; open: boolean; from: string; to: string }
/** One row per weekday (Sunday first) from the stored ranges; a day without a range is closed (09:00 to 17:00 prefilled). */
export function weekOf(schedule: readonly HoursRange[]): DayRow[] {
  return WEEKDAYS.map((day) => {
    const r = schedule.find((x) => x.day === day);
    return r ? { day, open: true, from: r.open, to: r.close } : { day, open: false, from: '09:00', to: '17:00' };
  });
}
export const scheduleOf = (week: readonly DayRow[]): HoursRange[] => week.filter((d) => d.open).map((d) => ({ day: d.day, open: d.from, close: d.to }));

/** Keywords typed one per line or comma separated (Arabic comma too); empty entries dropped. */
export const keywordsOf = (text: string): string[] => text.split(/[\n,،]+/).map((k) => k.trim()).filter(Boolean);

export type AssistantError = 'greeting' | 'away' | 'hours' | 'faqs' | 'faqsMax' | 'keywordsMax';
/** Client-side checks before saving (the API validates again). */
export function assistantErrors(s: AssistantSettings, faqs: readonly FaqItem[]): AssistantError[] {
  const out: AssistantError[] = [];
  if (s.greeting.enabled && !s.greeting.text.trim() && !s.greeting.textAr.trim()) out.push('greeting');
  if (s.businessHours.enabled && !s.businessHours.awayMessage.trim() && !s.businessHours.awayMessageAr.trim()) out.push('away');
  if (s.businessHours.enabled && !s.businessHours.schedule.length) out.push('hours');
  if (faqs.some((f) => !f.question.trim() || !f.answer.trim())) out.push('faqs');
  if (faqs.length > MAX_FAQS) out.push('faqsMax');
  if (s.handover.keywords.length > MAX_HANDOVER_KEYWORDS) out.push('keywordsMax');
  return out;
}

/** PUT /settings/assistant and /knowledge/faqs are owner/admin only; everyone else sees them read-only. */
export const canEditAssistant = (roles: readonly string[] | null | undefined): boolean => !!roles?.some((r) => r === 'customer_owner' || r === 'customer_admin');
